/**
 * Bulk enrichment engine (PAN-457).
 *
 * Orchestrates L1/L2/L3 enrichment for discovered sessions:
 *   - Filters sessions needing enrichment at the requested tier
 *   - Estimates API cost before running
 *   - Runs via bounded work pool for parallelism
 *   - Reports progress via optional callback
 */

import { findDiscoveredSessionIds, getDiscoveredSessionById } from '../../overdeck/discovered-sessions.js';
import type { ConversationFilter } from '../../overdeck/discovered-sessions.js';
import { Effect } from 'effect';
import { runWithPool } from '../work-pool.js';
import { createConfiguredClaudeApi, enrichSession, resolveEnrichmentModel } from './enrich-session.js';
import { embedSessions } from '../embeddings/index.js';
import type { EnrichSessionOptions } from './enrich-session.js';
import { getConversationsConfigSync } from '../../config-yaml.js';
import type { RuntimeConversationsConfig } from '../../config-yaml.js';
import type { EnrichmentTier } from './model-fallback.js';
import { isBackgroundFeatureEnabled } from '../../background-ai/features.js';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface EnrichOptions {
  /** Enrichment tier to apply (default: 1) */
  tier?: EnrichmentTier;
  /** Specific session IDs to enrich (default: all not yet enriched at tier) */
  sessionIds?: number[];
  /** Maximum concurrent enrichment tasks */
  maxParallel?: number;
  /** Structured filter used when sessionIds is omitted */
  filter?: ConversationFilter;
  /** Cap selected sessions after filtering */
  limit?: number;
  /** If true, skip sessions already enriched at this tier or higher */
  skipAlreadyEnriched?: boolean;
  /** If true, bypass the cost confirmation threshold */
  force?: boolean;
  /** Override the model used for enrichment (ignores tier-based model selection) */
  modelOverride?: string;
  /** Append custom text to the enrichment prompt */
  promptSuffix?: string;
  /** Read every JSONL line for literal full-transcript enrichment */
  fullTranscript?: boolean;
  /** Injected API caller for testing */
  callApi?: EnrichSessionOptions['callApi'];
  /** Preloaded conversations config for dashboard callers */
  config?: RuntimeConversationsConfig;
  /** Progress callback */
  onProgress?: (progress: EnrichProgress) => void | Promise<void>;
}

export interface EnrichProgress {
  processed: number;
  total: number;
  errors: number;
  elapsedMs: number;
  /** Per-session details for the session that just completed (optional) */
  session?: {
    sessionId: number;
    tier: EnrichmentTier;
    model: string;
    cost?: number;
    success: boolean;
    error?: string;
  };
}

export interface EnrichResult {
  enriched: number;
  skipped: number;
  errors: number;
  durationMs: number;
  estimatedCost: number;
  actualCost: number | null;
  embedded: number;
}

// ─── Cost estimation ──────────────────────────────────────────────────────────

/**
 * Rough cost estimate for enrichment batch.
 * Assumes ~500 input tokens per session + ~200 output tokens at Sonnet rate.
 * Used only for the cost confirmation threshold check.
 */
export function estimateEnrichmentCost(sessionCount: number, tier: EnrichmentTier): number {
  // Approximate token counts per session per tier
  const inputTokens = tier === 1 ? 300 : tier === 2 ? 700 : 2000;
  const outputTokens = tier === 1 ? 150 : tier === 2 ? 300 : 600;
  // Approximate cost per 1M tokens (blended input/output)
  const costPer1M = tier === 1 ? 0.80 : 3.00; // Haiku vs Sonnet
  return ((inputTokens + outputTokens) * sessionCount * costPer1M) / 1_000_000;
}

// ─── Session selection ────────────────────────────────────────────────────────

function selectSessionIdsForEnrichment(
  opts: EnrichOptions,
  tier: EnrichmentTier,
): number[] {
  if (opts.sessionIds && opts.sessionIds.length > 0) return opts.sessionIds;

  const baseFilter = { ...(opts.filter ?? {}), limit: opts.limit };
  const skipAlready = opts.skipAlreadyEnriched !== false; // default true
  if (skipAlready) return findDiscoveredSessionIds({ ...baseFilter, enrichmentLevelLessThan: tier });

  return findDiscoveredSessionIds(baseFilter);
}

// ─── Main bulk enrichment ─────────────────────────────────────────────────────

/**
 * Enrich discovered sessions at the specified tier.
 *
 * @param opts.tier               1=quick/Haiku, 2=detailed/Sonnet, 3=deep/configurable
 * @param opts.sessionIds         Optional specific session IDs (default: all not enriched)
 * @param opts.maxParallel        Override parallelism (default: from config)
 * @param opts.skipAlreadyEnriched Skip sessions already at this tier or higher (default: true)
 * @param opts.onProgress         Progress callback
 */
export async function enrichSessions(opts: EnrichOptions = {}): Promise<EnrichResult> {
  const startTs = Date.now();
  const result: EnrichResult = { enriched: 0, skipped: 0, errors: 0, durationMs: 0, estimatedCost: 0, actualCost: null, embedded: 0 };

  // NOTE: enrichSessions is invoked only by explicit user action (the dashboard
  // "enrich" job / RPC), never automatically — so low-cost mode does NOT gate it
  // (silently no-op'ing an explicit click is bad UX). Low-cost mode gates the
  // *automatic* paths: the auto-embed-on-deep branch below (sessionEmbeddings)
  // and the genuinely-background callers (titles, memory, compaction, TTS).
  // Cost is still recorded per enriched session for the Background AI cost view.

  const tier = opts.tier ?? 1;
  const config = opts.config ?? getConversationsConfigSync();
  const tierConfig = {
    quickModel: config.enrichment.quickModel,
    deepModel: config.enrichment.deepModel,
  };
  const maxParallel = opts.maxParallel ?? config.enrichment.maxParallel;
  const callApi = opts.callApi ?? createConfiguredClaudeApi({
    apiKeys: config.apiKeys,
    enabledProviders: config.enabledProviders,
  });
  const resolveModel = opts.callApi ? undefined : (model: string) => resolveEnrichmentModel(model, config.enabledProviders);

  const sessionIds = selectSessionIdsForEnrichment(opts, tier);

  if (sessionIds.length === 0) {
    result.durationMs = Date.now() - startTs;
    return result;
  }

  // Cost gate: check against threshold
  const estimatedCost = estimateEnrichmentCost(sessionIds.length, tier);
  result.estimatedCost = estimatedCost;
  const threshold = config.enrichment.costConfirmThreshold;
  if (estimatedCost > threshold && !opts.force) {
    // Callers can check the threshold themselves before calling.
    // We throw to make the gate explicit — CLI will catch and prompt user.
    throw new CostThresholdError(estimatedCost, threshold, sessionIds.length);
  }

  let processed = 0;
  let actualCost = 0;
  let actualCostCount = 0;
  const total = sessionIds.length;
  const enrichedIds: number[] = [];

  const taskStates = sessionIds.map((sessionId) => ({ sessionId, accounted: false }));
  const tasks = taskStates.map((state) => async () => {
    const session = getDiscoveredSessionById(state.sessionId);
    if (!session) {
      processed++;
      state.accounted = true;
      result.skipped++;
      await opts.onProgress?.({
        processed,
        total,
        errors: result.errors,
        elapsedMs: Date.now() - startTs,
      });
      return;
    }
    const sessionResult = await Effect.runPromise(enrichSession({
      sessionId: session.id,
      jsonlPath: session.jsonlPath,
      tier,
      modelOverride: opts.modelOverride,
      promptSuffix: opts.promptSuffix,
      fullTranscript: opts.fullTranscript,
      config: tierConfig,
      callApi,
      resolveModel,
    }));

    processed++;
    state.accounted = true;
    if (sessionResult.error) {
      result.errors++;
    } else {
      result.enriched++;
      enrichedIds.push(session.id);
      if (sessionResult.cost !== undefined) {
        actualCost += sessionResult.cost;
        actualCostCount++;
      }
    }

    try {
      await opts.onProgress?.({
        processed,
        total,
        errors: result.errors,
        elapsedMs: Date.now() - startTs,
        session: {
          sessionId: session.id,
          tier,
          model: sessionResult.model,
          cost: sessionResult.error ? undefined : sessionResult.cost ?? estimateEnrichmentCost(1, tier),
          success: !sessionResult.error,
          error: sessionResult.error,
        },
      });
    } catch {
      result.errors++;
    }
  });

  const taskResults = await Effect.runPromise(runWithPool(tasks, maxParallel));
  for (let i = 0; i < taskResults.length; i++) {
    const taskResult = taskResults[i];
    if (!(taskResult instanceof Error)) continue;
    const state = taskStates[i];
    if (!state.accounted) {
      processed++;
      result.errors++;
    }
  }

  if (tier >= 2 && config.embeddings && config.embeddingAutoOnDeep
      && isBackgroundFeatureEnabled('sessionEmbeddings')) {
    if (enrichedIds.length > 0) {
      const embedded = await embedSessions({ sessionIds: enrichedIds, config });
      result.embedded = embedded.embedded;
    }
  }

  result.actualCost = actualCostCount > 0 ? actualCost : null;
  result.durationMs = Date.now() - startTs;
  return result;
}

// ─── Cost threshold error ─────────────────────────────────────────────────────

export class CostThresholdError extends Error {
  constructor(
    public readonly estimatedCost: number,
    public readonly threshold: number,
    public readonly sessionCount: number,
  ) {
    super(
      `Estimated enrichment cost $${estimatedCost.toFixed(4)} exceeds threshold $${threshold.toFixed(2)} for ${sessionCount} sessions`,
    );
    this.name = 'CostThresholdError';
  }
}

// ─── Re-exports ───────────────────────────────────────────────────────────────

export { enrichSession, callClaudeApi } from './enrich-session.js';
export { selectModelForTier, maxMessagesForTier } from './model-fallback.js';
export type { EnrichmentTier, TierConfig } from './model-fallback.js';
